import React from 'react';
import { StickyContainer } from 'react-sticky';

import { Grid, Typography } from '@hbf/dsl';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { PricingStructure } from 'ha/modules/Pricing';
import {
  ListingCancellationPolicy,
  ListingFacilities,
} from '@ha/api/v2/types/Listing';
import { UserCardInfo } from 'ha/components/Redesign/UserCard';

import { StudentResidenceNoticeBanner } from 'ha/modules/PBSA/components';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';
import { LoadableGalleryLayout } from '../containers/GalleryLayout.lazy';
import { ListingHighlightsContainer as ListingHighlights } from '../containers/ListingHighlights';
import { ListingFacilitiesSectionContainer as ListingFacilitiesSection } from '../containers/ListingFacilitiesSection';
import { ListingPreferredTenantSectionContainer as ListingPreferredTenantSection } from '../containers/ListingPreferredTenantSection';

import { CancellationPolicySection } from '../components/CancellationPolicySection';
import { CharityInfoBanner } from '../components/CharityInfoBanner';
import { DocumentsRequired as DocumentsRequiredToBook } from '../components/DocumentsRequired';
import { FaqSection } from '../components/FaqSection';
import { ListingDescription } from '../components/ListingDescription';
import { RegistrationInfoSection } from '../components/RegistrationInfoSection';

import { ListingOverviewDesktopContainer as ListingOverviewDesktop } from './ListingOverviewDesktop';
import { ListingRentalConditionsSection } from './ListingRentalConditionsSection';

import { useDesiredDates } from '../hooks/useDesiredDates';
import { useLocalPricing } from '../hooks/useLocalPricing';
import { usePageRedesign } from '../hooks/usePageRedesign';
import { getAdvertiser } from '../selectors';

const useStyles = makeStyles()(theme => ({
  relative: {
    position: 'relative',
  },
  spacer: {
    margin: theme.spacing(2),
  },
  rentalConditionsContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  userCard: {
    marginLeft: -theme.spacing(3),
  },
}));

export interface Props {
  hasDescription: boolean;
  countryCode: string;
  facilities: ListingFacilities;
  cancellationPolicy: ListingCancellationPolicy;
  isBelongsToPartner: boolean;
  isCharitable?: boolean;
  isFreeListing?: boolean;
}

const ListingOverview: React.FC<Props> = ({
  cancellationPolicy,
  countryCode,
  facilities,
  hasDescription,
  isBelongsToPartner,
  isCharitable = false,
  isFreeListing = false,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const { md } = useMediaQuery();

  const { hasDesiredDates } = useDesiredDates();
  const { pricingStructure } = useLocalPricing();
  const withPageRedesign = usePageRedesign();
  const advertiser = useSelector(getAdvertiser);

  const { showStudentResidenceTags } = useFeatureFlags();
  return (
    <React.Fragment>
      {md && !withPageRedesign && <div className={classes.spacer} />}

      <Grid container item xs={12} data-test-locator="GalleryLayout section">
        <LoadableGalleryLayout />
      </Grid>

      <Grid item xs={12}>
        {(md || !withPageRedesign) && <div className={classes.spacer} />}

        <Grid container item xs={12}>
          <StickyContainer className={classes.relative}>
            {md && !withPageRedesign && <ListingOverviewDesktop />}
            <Grid
              container
              item
              md={withPageRedesign ? 12 : 8}
              xs={12}
              id="basics"
            >
              {!md && withPageRedesign && (
                <div className={classes.userCard}>
                  <UserCardInfo user={advertiser} />
                </div>
              )}

              {showStudentResidenceTags === 'on' &&
                facilities.student_housing === FacilitiesValues.Yes && (
                  <StudentResidenceNoticeBanner />
                )}

              <ListingHighlights />

              {isCharitable && <CharityInfoBanner />}

              <RegistrationInfoSection
                registration={facilities?.registration_possible ?? ''}
                countryCode={countryCode}
              />

              {hasDescription && (
                <Grid item xs={12}>
                  <ListingDescription />
                </Grid>
              )}

              <Grid item xs={12}>
                <ListingFacilitiesSection key="ListingFacilitiesSection" />
              </Grid>
              <Grid item xs={12}>
                <Typography.H3 data-test-locator="Listing/Preferred Tenant">
                  {T('Preferred tenant')}
                </Typography.H3>
                <ListingPreferredTenantSection />
              </Grid>
              <Grid item>
                <DocumentsRequiredToBook />
              </Grid>
              <Grid
                item
                xs={12}
                id="rental-conditions"
                key="listingRentalConditionsSection"
              >
                <Typography.H3 data-test-locator="Listing/Rental Conditions">
                  {T('listings.details.rental_conditions.title')}
                </Typography.H3>

                {!(isCharitable && isFreeListing) &&
                  pricingStructure === PricingStructure.instalment &&
                  !hasDesiredDates &&
                  !isBelongsToPartner && (
                    <Typography.Paragraph>
                      {T(
                        'Add your move-in dates to see the full payment overview',
                      )}
                    </Typography.Paragraph>
                  )}

                <div className={classes.rentalConditionsContainer}>
                  <ListingRentalConditionsSection
                    isCharitable={isCharitable}
                    isFreeListing={isFreeListing}
                  />
                </div>
              </Grid>

              {!(isFreeListing && isCharitable) && !isBelongsToPartner && (
                <CancellationPolicySection
                  cancellationPolicy={cancellationPolicy}
                />
              )}

              {!isBelongsToPartner && <FaqSection countryCode={countryCode} />}
            </Grid>
          </StickyContainer>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export { ListingOverview };
